import { useMemo } from "react";

/**
 * Compact SVG line chart of odometer readings.
 * `entries`: [{reading, recorded_at}]  — ordered ascending or descending.
 */
export function OdometerChart({ entries, unit = "km", testid }) {
    const data = useMemo(() => {
        const e = (entries || [])
            .filter((x) => typeof x.reading === "number")
            .map((x) => ({
                t: new Date(x.recorded_at).getTime() || Date.now(),
                r: x.reading,
            }))
            .sort((a, b) => a.t - b.t);
        return e;
    }, [entries]);

    if (data.length < 2) {
        return (
            <div
                className="grid h-32 place-items-center rounded-md border border-dashed border-zinc-300 px-3 text-xs text-zinc-500 dark:border-zinc-700"
                data-testid={testid || "odometer-chart-empty"}
            >
                Add at least two readings to see the trend.
            </div>
        );
    }

    const W = 600;
    const H = 140;
    const PAD = 24;
    const minT = data[0].t;
    const maxT = data[data.length - 1].t;
    const minR = Math.min(...data.map((d) => d.r));
    const maxR = Math.max(...data.map((d) => d.r));
    const sx = (t) => PAD + ((t - minT) / Math.max(1, maxT - minT)) * (W - 2 * PAD);
    const sy = (r) => H - PAD - ((r - minR) / Math.max(1, maxR - minR)) * (H - 2 * PAD);

    const path = data.map((p, i) => (i === 0 ? "M" : "L") + sx(p.t).toFixed(1) + "," + sy(p.r).toFixed(1)).join(" ");
    const last = data[data.length - 1];

    return (
        <div className="rounded-md border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900" data-testid={testid || "odometer-chart"}>
            <div className="mb-2 flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                    Odometer
                </div>
                <div className="font-mono text-sm tnum">
                    {last.r.toLocaleString()} {unit}
                </div>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="h-32 w-full">
                {/* axis baseline */}
                <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="currentColor" strokeOpacity="0.15" />
                <line x1={PAD} y1={PAD} x2={PAD} y2={H - PAD} stroke="currentColor" strokeOpacity="0.15" />
                {/* path */}
                <path d={path} fill="none" stroke="#E11D2E" strokeWidth="2" />
                {/* points */}
                {data.map((p, i) => (
                    <circle key={i} cx={sx(p.t)} cy={sy(p.r)} r="2.5" fill="#E11D2E" />
                ))}
            </svg>
        </div>
    );
}
